/**
 * @CODE:ERROR-001 - Sentry Error Tracking Initialization
 * Initializes Sentry error tracking with PII filtering and GDPR/CCPA compliance
 *
 * Features:
 * - 100% error sample rate (sampleRate: 1.0)
 * - PII filtering via beforeSend hook
 * - Customer ID hashing (SHA256)
 * - Cookie removal for privacy compliance
 * - Async script loading for performance
 */

(function() {
  'use strict';

  /**
   * SHA256 hash function for customer ID anonymization
   * @param {string} str - String to hash
   * @returns {Promise<string>} - SHA256 hash in hex format
   */
  async function sha256(str) {
    const buffer = new TextEncoder().encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * PII filtering function for Sentry events
   * Removes sensitive data before sending to Sentry
   * @param {Object} event - Sentry event object
   * @returns {Object|null} - Filtered event or null to drop
   */
  async function beforeSend(event) {
    if (!event) return event;

    // Remove cookies
    if (event.request) {
      delete event.request.cookies;
      if (event.request.headers) {
        delete event.request.headers.cookie;
      }

      // Remove query parameters with PII
      if (event.request.url) {
        try {
          const url = new URL(event.request.url);
          // Remove email from query params
          url.searchParams.delete('email');
          url.searchParams.delete('customer_email');
          event.request.url = url.toString();
        } catch (e) {
          // Invalid URL, keep as is
        }
      }
    }

    // Remove email from user object
    if (event.user) {
      delete event.user.email;
      delete event.user.ip_address;

      // Hash customer ID if present
      if (event.user.id) {
        try {
          event.user.id = await sha256(event.user.id);
        } catch (e) {
          // If hashing fails, remove the ID
          delete event.user.id;
        }
      }
    }

    // Remove sensitive headers
    if (event.request && event.request.headers) {
      const sensitiveHeaders = [
        'authorization',
        'x-customer-id',
        'x-api-key',
        'x-auth-token',
      ];
      sensitiveHeaders.forEach(header => {
        delete event.request.headers[header];
      });
    }

    // Filter breadcrumbs for sensitive information
    if (event.breadcrumbs && Array.isArray(event.breadcrumbs)) {
      event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
        if (breadcrumb.message) {
          // Remove customer_id from messages
          breadcrumb.message = breadcrumb.message.replace(/customer_id[:\s]+\d+/gi, 'customer_id:[REDACTED]');
          breadcrumb.message = breadcrumb.message.replace(/email[:\s]+[\w\.\-@]+/gi, 'email:[REDACTED]');
        }
        if (breadcrumb.data) {
          // Remove sensitive data from breadcrumb data
          Object.keys(breadcrumb.data).forEach(key => {
            if (key.toLowerCase().includes('email') || key.toLowerCase().includes('customer')) {
              delete breadcrumb.data[key];
            }
          });
        }
        return breadcrumb;
      });
    }

    // @CODE:SPEC-FIX-JAPAN-001 - Enhanced third-party error classification
    // Two-stage classification:
    // 1. Check if error is from third-party source
    // 2. If third-party but unhandled in our code → our responsibility (our-code)
    const thirdPartyDomains = [
      'cdn.shopify.com/extensions',
      'cdn-asset.optimonk.com',
      'gs-cdn.optimonk.com',  // OptiMonk CDN
      'littledata',
      'colibrius',
      'easify',
      'avada',
      'unpkg.com',
      'cdnjs.cloudflare.com',
      'googleapis.com',
      'googletagmanager.com',
      // CartIntegration and btoa related domains
      'cdn.shopify.com/s/files',
      'cdn.shopifycdn.net',
      'cartintegration',
      // Additional third-party apps (Korea region errors)
      'omnisend',
      'inshop',
      'soundestlink.com',
      'wt.omnisendlink.com',
      'browser-utils/build',
      // Judge.me
      'judge.me',
      'cdn.judge.me',
      // Hotjar
      'static.hotjar.com',
      'script.hotjar.com',
      'vars.hotjar.com',
      // JFClientSDK (OptiMonk)
      'jfclientsdk',
    ];

    const thirdPartyApps = [
      'optimonk',
      'littledata',
      'easify',
      'avada',
      'klaviyo',
      'judgeme',
      'judge.me',
      'jdgm',  // Judge.me jQuery namespace
      'loox',
      'hotjar',
      // Additional third-party integrations
      'cartintegration',
      'cartintegrations',
      'omnisend',
      'OptiMonkRegistry',
      'JFClientSDK',
    ];

    // Stage 1: Check if error is from third-party source
    let isThirdPartySource = false;
    const exception = event.exception?.values?.[0];

    if (exception) {
      const stacktrace = exception.stacktrace?.frames || [];

      // Check stack trace frames
      isThirdPartySource = stacktrace.some(frame => {
        const filename = frame.filename || frame.absPath || '';
        return thirdPartyDomains.some(domain => filename.includes(domain));
      });

      // Check error message for third-party app names
      if (!isThirdPartySource && exception.value) {
        isThirdPartySource = thirdPartyApps.some(app =>
          exception.value.toLowerCase().includes(app)
        );
      }
    }

    // Stage 2: If third-party source, check if we need to handle it
    // Third-party errors that are unhandled in our code = our responsibility
    const isHandled = exception?.mechanism?.handled !== false;
    const hasOurCodeInStack = exception?.stacktrace?.frames?.some(frame => frame.in_app === true) || false;

    // Classification logic:
    // - Not third-party source → our-code
    // - Third-party source but unhandled AND our code in stack → our-code (need error handling)
    // - Third-party source and (handled OR no our code) → third-party (nothing we can do)
    let errorSource;
    if (!isThirdPartySource) {
      errorSource = 'our-code';
    } else if (!isHandled && hasOurCodeInStack) {
      // Third-party error but we're calling it without proper error handling
      errorSource = 'our-code';
      event.tags = event.tags || {};
      event.tags.error_handling_needed = 'true';  // Flag for adding try-catch
    } else {
      errorSource = 'third-party';
    }

    // Add classification tags
    event.tags = event.tags || {};
    event.tags.error_source = errorSource;

    // @CODE:SENTRY-IGNORE-THIRD-PARTY - Drop third-party errors completely
    // Third-party errors are handled by our error boundary system (third-party-loader.js)
    // We don't need to track them in Sentry as they clutter the dashboard
    if (errorSource === 'third-party') {
      // Return null to completely drop this event (not sent to Sentry)
      // This prevents third-party errors from cluttering the Sentry dashboard
      // Our error boundary system (third-party-loader.js) already handles these gracefully
      return null;
    }

    // @CODE:FIX-SENTRY-ANDROID-WEBVIEW - Filter Android WebView lifecycle errors
    // These errors occur when Android app's WebView calls Java bridge methods after
    // the bridge object has been deallocated. Cannot be fixed in our JavaScript code.
    // Related issues: SYRS-SHOPIFY-22, SYRS-SHOPIFY-33, SYRS-SHOPIFY-20
    const isAndroidWebView = /Android.*wv\)|WebView/i.test(navigator.userAgent);
    if (isAndroidWebView && exception) {
      const androidBridgeErrors = [
        'Java bridge method',
        'JavascriptInterface',
        'android.webkit',
        'WebViewJavascriptBridge',
        "Cannot call method on null",
        "object is not a function"
      ];
      const isAndroidBridgeError = androidBridgeErrors.some(pattern =>
        (exception.value || '').toLowerCase().includes(pattern.toLowerCase()) ||
        (exception.type || '').toLowerCase().includes(pattern.toLowerCase())
      );
      if (isAndroidBridgeError) {
        return null; // Drop Android WebView bridge errors
      }
    }

    return event;
  }

  /**
   * Sentry Configuration Object
   */
  const SentryConfig = {
    dsn: window.__SENTRY_DSN__ || null,
    sampleRate: 1.0, // 100% error sampling
    tracesSampleRate: 0.1, // 10% performance tracing
    environment: window.__SENTRY_ENV__ || 'production',
    beforeSend: beforeSend,
    maxBreadcrumbs: 50,
    maxValueLength: 1024,
    integrations: [
      // Error reporting
      'inboundFilters',
      'captureUnhandledRejections',
      'contextLines',
      'httpContext',
      'linkedErrors',
    ],
    denyUrls: [
      // Ignore errors from browser extensions
      /^chrome:\/\//,
      /^moz-extension:\/\//,
      /^webkit-masked-url:\/\//,
    ],
  };

  // Export for testing
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = SentryConfig;
  }

  // Initialize Sentry in browser context
  if (typeof window !== 'undefined' && window.Sentry) {
    window.Sentry.init(SentryConfig);

    // Add Shopify context
    window.Sentry.setContext('shopify', {
      environment: window.__SENTRY_ENV__ || 'production',
      release: window.__SENTRY_RELEASE__ || 'unknown',
    });

    // Detect and add page type
    const pageType = (function() {
      if (window.location.pathname.includes('/products/')) return 'product';
      if (window.location.pathname.includes('/collections/')) return 'collection';
      if (window.location.pathname.includes('/cart')) return 'cart';
      if (window.location.pathname.includes('/checkout')) return 'checkout';
      if (window.location.pathname === '/') return 'home';
      return 'other';
    })();

    window.Sentry.setTag('page_type', pageType);
    window.Sentry.setTag('page_url', window.location.pathname);

    // Track navigation as breadcrumbs
    let lastUrl = location.href;
    new MutationObserver(() => {
      const currentUrl = location.href;
      if (currentUrl !== lastUrl) {
        window.Sentry.addBreadcrumb({
          category: 'navigation',
          message: `Navigated to ${currentUrl}`,
          level: 'info',
        });
        lastUrl = currentUrl;
      }
    }).observe(document, { subtree: true, childList: true });

    // Track button clicks as breadcrumbs
    document.addEventListener('click', function(e) {
      const target = e.target.closest('button, a, [role="button"]');
      if (target) {
        const text = target.textContent.trim().substring(0, 50);
        const id = target.id ? `#${target.id}` : '';
        const classes = target.className ? `.${target.className.split(' ')[0]}` : '';

        window.Sentry.addBreadcrumb({
          category: 'ui.click',
          message: `Clicked: ${text || id || classes}`,
          level: 'info',
          data: {
            element: target.tagName.toLowerCase() + id + classes,
          }
        });
      }
    }, true);
  }

  return SentryConfig;
})();
